<template>
    <MultiSelect
        v-model="tempSelection"
        :options="props.options"
        :placeholder="props.placeholder"
        :disabled="!isInteractive"
        asChild
        :track-by="props.trackBy"
        :option-label="props.optionLabel">
        <!-- Trigger button -->
        <Button
            type="button"
            variant="outline"
            :disabled="!isInteractive"
            :style="{ width: buttonWidth }"
            class="h-9"
            :class="[props.slimDropdownButton ? 'px-3' : 'px-4', 'inline-flex min-w-max justify-center items-center text-sm font-medium']">
            <Filter :class="['h-4 w-4 mr-1', hasSelection ? 'text-primary' : '']" :fill="hasSelection ? 'currentColor' : 'none'" />
            <span class="px-1">
                <slot name="displayTitle" :selectedCount="appliedSelection.length" :placeholder="props.placeholder">
                    {{ appliedSelection.length ? `${appliedSelection.length} selected` : props.placeholder }}
                </slot>
            </span>
            <ChevronDown class="-mr-1 ml-2 h-5 w-5" />
        </Button>

        <!-- Footer actions -->
        <template #footer>
            <div class="space-y-3">
                <!-- Operator controls -->
                <div v-if="props.enableOperators" class="p-2">
                    <div class="mb-2 text-sm font-medium text-muted-foreground">Operator</div>
                    <div class="flex gap-2">
                        <Button
                            type="button"
                            :variant="tempOperator === 'or' ? 'default' : 'outline'"
                            size="sm"
                            @click="setOperator('or')"
                            :disabled="tempSelection.length <= 1 || !isInteractive">
                            OR
                        </Button>
                        <Button
                            type="button"
                            :variant="tempOperator === 'and' ? 'default' : 'outline'"
                            size="sm"
                            @click="setOperator('and')"
                            :disabled="tempSelection.length <= 1 || !isInteractive">
                            AND
                        </Button>
                    </div>
                </div>

                <!-- Action buttons -->
                <div class="pt-3 flex justify-between items-center border-t">
                    <Button variant="ghost" size="sm" @click="onManageEntity" :disabled="!isInteractive">
                        <Pencil class="h-4 w-4 mr-1" />
                        {{ props.manageButtonText }}
                    </Button>
                    <Button variant="default" size="sm" @click="applyChanges" :disabled="!canApply || !isInteractive"> Apply </Button>
                </div>
            </div>
        </template>

        <!-- Forward custom option rendering -->
        <template #option="{ option }">
            <slot name="option" :option="option" />
        </template>
    </MultiSelect>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { MultiSelect } from '@/components/ui/multi-select';
import { Button } from '@/components/ui/button';
import { Pencil, ChevronDown, Filter } from 'lucide-vue-next';

const props = defineProps({
    options: { type: Array, required: true },
    modelValue: {
        type: Object,
        default: () => ({ selected: [], operator: 'or' })
    },
    enableOperators: { type: Boolean, default: true },
    placeholder: { type: String, default: 'Select options' },
    manageButtonText: { type: String, default: 'Manage' },
    disabled: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    slimDropdownButton: { type: Boolean, default: false },
    buttonWidth: { type: String, default: 'fit-content' },
    trackBy: { type: String, default: null },
    optionLabel: { type: [String, Function], default: null }
});

const emit = defineEmits(['update:modelValue', 'apply', 'onManageEntity']);

// Local temp & applied states
const tempSelection = ref([...props.modelValue.selected]);
const tempOperator = ref(props.modelValue.operator);
const appliedSelection = ref([...props.modelValue.selected]);
const appliedOperator = ref(props.modelValue.operator);

// Watch for external modelValue changes
watch(
    () => props.modelValue,
    ({ selected = [], operator = 'or' }) => {
        tempSelection.value = [...selected];
        appliedSelection.value = [...selected];
        tempOperator.value = operator;
        appliedOperator.value = operator;
    },
    { deep: true }
);

// Computed flags
const unsavedChanges = computed(
    () => JSON.stringify(tempSelection.value) !== JSON.stringify(appliedSelection.value) || tempOperator.value !== appliedOperator.value
);
const canApply = computed(() => unsavedChanges.value);
const hasSelection = computed(() => appliedSelection.value.length > 0);
const isInteractive = computed(() => !props.disabled && !props.loading);

// Set (and immediately apply) operator
function setOperator(op) {
    tempOperator.value = op;
}

// Apply both selection and operator
function applyChanges() {
    appliedSelection.value = [...tempSelection.value];
    appliedOperator.value = tempOperator.value;
    emit('update:modelValue', {
        selected: appliedSelection.value,
        operator: appliedOperator.value
    });
    emit('apply', {
        selected: appliedSelection.value,
        operator: appliedOperator.value
    });
}

function onManageEntity() {
    emit('onManageEntity');
}
</script>
